import React from "react";
import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import ProcessGraphLink from "./ProcessGraphLink";
import type { ProcessOverview } from "./processOverview";
import { formatDuration } from "../../utils/format";
import { getAllocationColor, INSIGNIFICANT_COLOR } from "../analytics/palette";

import TimeChange from "./TimeChange";
import { useExpandableList } from "../../utils/useExpandableList";
import { HelpHint } from "../processes/processSidebarUi";

const VISIBLE_PROCESSES = 5;

export default function ProcessAllocationBars({
  processes,
  previousProcesses,
  comparisonLabel,
  comparisonHelp,
  selectedSlug,
  onSelect,
  userIds,
}: {
  processes: ProcessOverview["processes"];
  previousProcesses?: ProcessOverview["processes"];
  comparisonLabel?: string;
  comparisonHelp?: string;
  selectedSlug: string | null;
  onSelect: (slug: string) => void;
  userIds: string[] | "all";
}) {
  const { t } = useTranslation();
  const { expanded, changeExpanded, listProps } = useExpandableList();
  // A sixth process takes the same space as Other, so only group two or more.
  const canExpand = processes.length > VISIBLE_PROCESSES + 1;
  const remaining = processes.slice(VISIBLE_PROCESSES);
  const rows = (expanded || !canExpand ? processes : processes.slice(0, VISIBLE_PROCESSES)).map(
    (row, index) => ({
      ...row,
      color: getAllocationColor(index),
    }),
  );
  const other =
    !expanded && canExpand
      ? {
          name: t("dashboard.other_processes"),
          slug: null,
          seconds: remaining.reduce((sum, row) => sum + row.seconds, 0),
          share: remaining.reduce((sum, row) => sum + row.share, 0),
          color: INSIGNIFICANT_COLOR,
        }
      : null;
  // Bars are shares of all the time, not of the longest row: they match the percentages and
  // keep their length when the list expands.
  const visibleRows = other ? [...rows, other] : rows;
  const previousSeconds = new Map(previousProcesses?.map(row => [row.slug, row.seconds]));
  const previousOther = remaining.reduce(
    (sum, row) => sum + (previousSeconds.get(row.slug) ?? 0),
    0,
  );
  // A column of dashes says nothing, so compare only when some process has a baseline.
  const showChange = processes.some(row => (previousSeconds.get(row.slug) ?? 0) > 0);
  const columns = showChange
    ? "grid grid-cols-[minmax(0,1fr)_4.5rem] items-center gap-x-4 gap-y-2 @[440px]/bars:grid-cols-[minmax(0,1fr)_7.5rem_4.5rem] @[720px]/bars:grid-cols-[minmax(0,2fr)_minmax(3rem,1fr)_7.5rem_4.5rem]"
    : "grid grid-cols-1 items-center gap-x-4 gap-y-2 @[440px]/bars:grid-cols-[minmax(0,1fr)_7.5rem] @[720px]/bars:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)_7.5rem]";

  return (
    <div className="@container/bars flex flex-1 flex-col">
      <div
        className={`${columns} overflow-y-hidden [scrollbar-gutter:stable] border-b border-outline py-3 px-2 @[440px]/bars:pr-14 text-[10px] font-medium uppercase tracking-wider text-text-mid`}
      >
        <span aria-hidden="true" className="col-span-full @[440px]/bars:col-span-1">
          {t("dashboard.process")}
        </span>
        <span aria-hidden="true" className="hidden @[720px]/bars:block" />
        <span aria-hidden="true" className="text-right">
          {t("dashboard.total_time")}
        </span>
        {showChange && (
          <span className="text-right">
            {comparisonHelp ? (
              <HelpHint label={t("dashboard.delta_previous")} text={comparisonHelp} standalone>
                {t("dashboard.delta_previous")}
              </HelpHint>
            ) : (
              <span aria-hidden="true">{t("dashboard.delta_previous")}</span>
            )}
          </span>
        )}
      </div>
      <ul {...listProps} className="overflow-y-auto [scrollbar-gutter:stable]">
        {visibleRows.map(row => (
          <li
            key={row.slug ?? "other"}
            className="relative flex items-center border-b border-outline/70 last:border-0"
          >
            <button
              type="button"
              aria-label={row.name}
              aria-pressed={row.slug === null ? undefined : selectedSlug === row.slug}
              aria-expanded={row.slug === null ? false : undefined}
              onClick={() => (row.slug === null ? changeExpanded(true) : onSelect(row.slug))}
              className={`${columns} min-h-12 min-w-0 flex-1 rounded-md px-2 py-3 text-left text-sm transition-colors focus-visible:outline-info ${row.slug !== null && selectedSlug === row.slug ? "bg-info/10 text-info" : "text-text-mid hover:bg-muted-surface"}`}
            >
              <span className="col-span-full flex min-w-0 items-center gap-2.5 pr-12 @[440px]/bars:col-span-1 @[440px]/bars:pr-0">
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: row.color }}
                  aria-hidden="true"
                />
                <span className="min-w-0 flex-1">
                  <span className="break-words leading-snug">{row.name}</span>
                  <span
                    className="mt-2 block h-1.5 overflow-hidden rounded-sm bg-muted-surface @[720px]/bars:hidden"
                    aria-hidden="true"
                  >
                    <span
                      className="block h-full rounded-sm"
                      style={{
                        width: `${row.share * 100}%`,
                        backgroundColor: row.color,
                      }}
                    />
                  </span>
                </span>
              </span>
              <span
                className="hidden h-3.5 overflow-hidden rounded-sm bg-muted-surface @[720px]/bars:block"
                aria-hidden="true"
              >
                <span
                  className="block h-full rounded-sm"
                  style={{
                    width: `${row.share * 100}%`,
                    backgroundColor: row.color,
                  }}
                />
              </span>
              <span className="text-right font-medium tabular-nums @[440px]/bars:whitespace-nowrap text-text">
                {formatDuration(row.seconds)}{" "}
                {/* A slot as wide as "(99%)" lines durations up without pushing shares far from them. */}
                <span className="block font-normal text-text-mid @[440px]/bars:inline-block @[440px]/bars:min-w-11">
                  ({row.share > 0 && row.share < 0.005 ? "<1" : Math.round(row.share * 100)}%)
                </span>
              </span>
              {showChange && (
                <TimeChange
                  current={row.seconds}
                  previous={
                    row.slug === null ? previousOther : (previousSeconds.get(row.slug) ?? 0)
                  }
                  label={comparisonLabel}
                />
              )}
            </button>
            {row.slug === null ? (
              <span className="hidden w-12 shrink-0 @[440px]/bars:block" />
            ) : (
              <ProcessGraphLink
                slug={row.slug}
                name={row.name}
                userIds={userIds}
                className="absolute right-0 top-2 mr-1 [&_svg]:size-3.5 @[440px]/bars:static @[440px]/bars:ml-3"
              />
            )}
          </li>
        ))}
      </ul>
      {canExpand && (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => changeExpanded(!expanded)}
          className="mt-auto inline-flex items-center gap-2 self-start pt-5 text-sm font-medium text-info hover:underline focus-visible:outline-info"
        >
          {expanded
            ? t("dashboard.show_less_apps")
            : t("dashboard.show_all_processes", { count: processes.length })}
          <ChevronDown className={`h-4 w-4 ${expanded ? "rotate-180" : ""}`} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
